import React, { FC, useState } from 'react';
import cn from 'classnames';

import { ProgAiOOSContribution } from 'shared/generated-models';
import { Icon, Text, TextButton } from 'shared/components/ui';
import { datePeriod } from 'features/candidate/utils';

import styles from './index.module.scss';

interface Props {
	data: ProgAiOOSContribution;
	firstName?: string;
	githubHandle?: string;
	className?: string;
}

const SHOW_TAGS_COUNT = 5;

export const Repository: FC<Props> = ({ data, firstName, githubHandle, className }: Props) => {
	const [isOpen, setIsOpen] = useState(false);

	const handleOpen = () => {
		setIsOpen(open => !open);
	};

	const formatContributionsDescription = (text: string) => {
		if (!text) return text;

		let replacement = firstName ? firstName.charAt(0).toUpperCase() + firstName.slice(1) : githubHandle;
		if (!replacement) return text;

		if (/^(The|the|This|this) user /.test(text)) {
			return text.replace(/^(The|the|This|this) user /, `${replacement} `);
		}

		if (/^(The|the|This|this) user's /.test(text)) {
			return text.replace(/^(The|the|This|this) user's /, `${replacement}'s `);
		}

		return text;
	};

	return (
		<div className={cn(styles.repository, className)}>
			<div>
				<div className={styles.repositoryTop}>
					{data.github_url && (
						<a href={data.github_url} className={styles.repositoryLink} target="_blank" rel="noreferrer">
							{data.github_url.substring(19, data.github_url.length)}
						</a>
					)}
					<Text component="p" color="grey">
						{datePeriod(data.first_commit_date, data.last_commit_date)}
					</Text>
				</div>
				{data.description && (
					<div className={styles.repositoryDescription}>
						<Text variant="inter/14/regular" color="grey">
							{data.description}
						</Text>
					</div>
				)}
			</div>
			<div className={styles.sections}>
				{data.user_role && (
					<div className={styles.section}>
						<Text variant="inter/14/medium" color="darker_grey">
							Role in this project:
						</Text>
						<div className={styles.role}>
							<Icon icon="user" size={20} />
							<Text variant="inter/14/regular" color="darker_grey">
								{data.user_role}
							</Text>
						</div>
					</div>
				)}
				{data.summary && (
					<div className={styles.section}>
						<Text variant="inter/14/medium" color="darker_grey">
							Contributions:
						</Text>
						<Text variant="inter/14/regular" color="black">
							{data.summary}
						</Text>
					</div>
				)}
				{data.contribution_description && (
					<div className={styles.section}>
						<Text variant="inter/14/medium" color="darker_grey">
							Contributions summary:
						</Text>
						<Text variant="inter/14/regular" color="black">
							{formatContributionsDescription(data.contribution_description)}
						</Text>
					</div>
				)}
			</div>
			{data.topics && (
				<div className={styles.repositoryContributions}>
					{data.topics.slice(0, SHOW_TAGS_COUNT).map(topic => (
						<Text
							variant="inter/12/medium"
							color="dark_green"
							className={styles.repositoryContribution}
							key={topic}>
							{topic}
						</Text>
					))}
					{isOpen &&
						data.topics.slice(SHOW_TAGS_COUNT, data.topics.length).map(topic => (
							<Text
								variant="inter/12/medium"
								color="dark_green"
								className={styles.repositoryContribution}
								key={topic}>
								{topic}
							</Text>
						))}
					{data.topics.length > 5 && (
						<TextButton onClick={handleOpen}>
							{isOpen ? 'See less' : `+${data.topics.length - SHOW_TAGS_COUNT} more`}
						</TextButton>
					)}
				</div>
			)}
		</div>
	);
};
